import { toast } from "react-toastify";
import { useMutation, useQuery } from "@tanstack/react-query";
import { UserService } from "@/api";

/**
 * Default error handler to display error messages using react-toastify.
 * @param {object} err - The error object from the mutation.
 */
const defaultError = (err) => {
  const message = err?.response?.data?.message || err?.message || "An error occurred. Please try again.";
  toast.error(message);
};

/**
 * Custom hook for handling user login.
 * It uses the `useMutation` hook to send the login request and handle the response.
 * It also allows modification of the request data (like encryption) before sending.
 *
 * @param {function} onSuccess - Callback to run if the login request succeeds.
 * @param {function} [onError=defaultError] - Callback to run if the login request fails.
 * @param {function} [modifyRequest] - Function to modify request data before sending (default encrypt).
 */
const useUserLogin = (onSuccess, onError = defaultError) => {
  return useMutation({
    mutationFn: (data) => {
      return UserService.login(data);
    },
    onSuccess,
    onError,
  });
};

/**
 * Custom hook for handling forgot password functionality.
 * It uses `useMutation` to send the request to reset the user's password.
 * The request data can be modified before sending (e.g., encryption).
 *
 * @param {function} onSuccess - Callback to run if the forgot password request succeeds.
 * @param {function} [onError=defaultError] - Callback to run if the forgot password request fails.
 * @param {function} [modifyRequest] - Function to modify request data before sending (default encrypt).
 */
const useForgotPassword = (onSuccess, onError = defaultError) => {
  return useMutation({
    mutationFn: (data) => {
      return UserService.forgotPassword(data);
    },
    onSuccess,
    onError,
  });
};

/**
 * Custom hook for handling password changes.
 * It utilizes the `useMutation` hook to send password update requests and manage success or error responses.
 *
 * @param {function} onSuccess - Callback to execute when the mutation is successful.
 * @param {function} [onError=defaultError] - Callback to execute when the mutation fails.
 *
 * @returns {object} Mutation object from `useMutation`.
 */
const useChangePassword = (onSuccess, onError = defaultError) => {
  return useMutation({
    mutationFn: (data) => {
      return UserService.changePassword(data);
    },
    onSuccess,
    onError,
  });
};

/**
 * Custom hook for handling profile edits.
 * It utilizes the `useMutation` hook to send profile update requests and manage success or error responses.
 *
 * @param {function} onSuccess - Callback to execute when the mutation is successful.
 * @param {function} [onError=defaultError] - Callback to execute when the mutation fails.
 *
 * @returns {object} Mutation object from `useMutation`.
 */
const useEditProfile = (onSuccess, onError = defaultError) => {
  return useMutation({
    mutationFn: (data) => {
      return UserService.editProfile(data);
    },
    onSuccess,
    onError,
  });
};

/**
 * Custom hook for fetching user profile.
 * It utilizes the `useQuery` hook to retrieve profile data.
 *
 * @param {function} onSuccess - Callback to execute when the query is successful.
 * @param {function} [onError=defaultError] - Callback to execute when the query fails.
 *
 * @returns {object} Query object from `useQuery`.
 */
const useGetProfile = (onSuccess, onError = defaultError) => {
  return useQuery({
    queryKey: ["profile-data"], // Query key
    queryFn: () => {
      return UserService.getProfile();
    }, // Query function
    onSuccess: (data) => {
      onSuccess(data);
    }, // Success callback
    onError: (error) => {
      onError(error);
    }, // Error callback
    retry: 2, // Allow retry for transient failures
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000), // Exponential backoff
    staleTime: 30000, // Consider data fresh for 30 seconds
    cacheTime: 300000, // Cache for 5 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });
};

/**
 * Custom hook for handling user logout.
 * It uses the `useMutation` hook to send the logout request.
 *
 * @param {function} onSuccess - Callback to run if the logout request succeeds.
 * @param {function} [onError=defaultError] - Callback to run if the logout request fails.
 * @returns {object} Mutation object from `useMutation`.
 */
const useLogout = (onSuccess, onError = defaultError) => {
  return useMutation({
    mutationFn: () => {
      return UserService.logout();
    },
    onSuccess,
    onError,
  });
};

/**
 * Custom hook for handling email verification OTP sending.
 * It uses the `useMutation` hook to send OTP to the provided email.
 *
 * @param {function} onSuccess - Callback to run if the send OTP request succeeds.
 * @param {function} [onError=defaultError] - Callback to run if the send OTP request fails.
 * @returns {object} Mutation object from `useMutation`.
 */
const useSendEmailVerificationOtp = (onSuccess, onError = defaultError) => {
  return useMutation({
    mutationFn: (data) => {
      return UserService.sendEmailVerificationOtp(data);
    },
    onSuccess,
    onError,
  });
};

/**
 * Custom hook for handling email OTP verification.
 * It uses the `useMutation` hook to verify the OTP for email verification.
 *
 * @param {function} onSuccess - Callback to run if the verify OTP request succeeds.
 * @param {function} [onError=defaultError] - Callback to run if the verify OTP request fails.
 * @returns {object} Mutation object from `useMutation`.
 */
const useVerifyEmailOtp = (onSuccess, onError = defaultError) => {
  return useMutation({
    mutationFn: (data) => {
      return UserService.verifyEmailOtp(data);
    },
    onSuccess,
    onError,
  });
};

export {
  useUserLogin,
  useForgotPassword,
  useChangePassword,
  useGetProfile,
  useEditProfile,
  useLogout,
  useSendEmailVerificationOtp,
  useVerifyEmailOtp,
};
